import { Contract } from "@/types/contract";
import { objectToSnake } from "ts-case-convert";

export const joinStringsByDelimiter = (first: string, second: string | number, delimiter = ","): string => {
  if (first === "") {
    return `${second}`;
  }
  if (second === "") {
    return first;
  }
  return `${first}${delimiter} ${second}`;
};

const toSeconds = (milliseconds?: number) => {
  if (milliseconds === 0) {
    return 0;
  } else {
    return milliseconds ? milliseconds / 1000 : null;
  }
};

export interface ContractOptions {
  finishContract?: boolean;
  generateContract?: boolean;
  save?: boolean;
  fromOldApplication?: boolean;
  createInSap?: boolean;
  handledByStaff?: boolean;
  findDistributor?: boolean;
}

export const mapToBackend = (uuid: string, contract: Contract, contractOptions: ContractOptions) => {
  const {
    finishContract = false,
    generateContract = false,
    save = false,
    fromOldApplication = false,
    createInSap = false,
    handledByStaff = false,
    findDistributor = false,
  } = contractOptions;
  const { supplyPoint } = contract;
  const newSupplyPoints = [
    {
      ...supplyPoint,
      trafficDate: toSeconds(supplyPoint.trafficDate?.getTime()),
      previousContract: {
        ...supplyPoint.previousContract,
        validTo: toSeconds(supplyPoint.previousContract?.validTo?.getTime()),
      },
    },
  ];

  return objectToSnake({
    contract: {
      ...contract,
      // fixes duplicate person on BE
      executivePerson: undefined,
      // fixes duplicate timestamps on BE
      timestamps: undefined,
      // fixes electrical appliances on BE
      supplyPoint: undefined,
      customer: {
        ...contract.customer,
        birthDate: toSeconds(contract.customer.birthDate?.getTime()),
        deathDate: toSeconds(contract.customer.deathDate?.getTime()),
      },
      executivePeople: contract.executivePeople.map(e => ({
        ...e,
        birthDate: toSeconds(e.birthDate?.getTime()),
      })),
      previousCustomer: {
        ...contract.previousCustomer,
        birthDate: toSeconds(contract.previousCustomer?.birthDate?.getTime()),
      },
      supplyPoints: newSupplyPoints,
      // BE 500 when id is null, must be undefined for new record
      meterReadings: contract.meterReadings.map(r => ({ ...r, id: r.id ?? undefined })),
      uuid,
    },
    finishContract,
    generateContract,
    save,
    fromOldApplication,
    createInSap,
    handledByStaff,
    findDistributor,
  });
};

export interface ContractRequestOptions {
  generateContractRequest?: boolean;
  save?: boolean;
  finishRequest?: boolean;
}

export const mapToBackendContractRequest = (uuid: string, contract: Contract, contractRequestOptions: ContractRequestOptions) => {
  const { generateContractRequest = false, save, finishRequest = false } = contractRequestOptions;

  const supplyPoint = contract.supplyPoint;
  const newSupplyPoints = [
    {
      ...supplyPoint,
      trafficDate: toSeconds(supplyPoint.trafficDate?.getTime()),
    },
  ];

  return objectToSnake({
    contractRequest: {
      ...contract,
      // fixes duplicate person on BE
      executivePerson: undefined,
      // fixes duplicate timestamps on BE
      timestamps: undefined,
      customer: {
        ...contract.customer,
        birthDate: toSeconds(contract.customer.birthDate?.getTime()),
        deathDate: toSeconds(contract.customer.deathDate?.getTime()),
      },
      executivePeople: contract.executivePeople.map(e => ({
        ...e,
        birthDate: toSeconds(e.birthDate?.getTime()),
      })),
      // BE 500 when id is null, must be undefined for new record
      meterReadings: contract.meterReadings.map(r => ({ ...r, id: r.id ?? undefined })),
      supplyPoint: undefined,
      supplyPoints: newSupplyPoints,
      uuid,
      baseDate: toSeconds(contract.baseDate?.getTime()),
      baseDateFrom: toSeconds(contract.baseDateFrom?.getTime()),
      baseDateTo: toSeconds(contract.baseDateTo?.getTime()),
      contractDetails: null,
    },
    generateContractRequest,
    save,
    finishRequest,
  });
};
